import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ShareButtons from '../components/ShareButtons';
import RelatedVillageNews from '../components/RelatedVillageNews';
import { buildApiUrl } from '../utils/api/config';
import { newsAPI } from '../utils/api/newsAPI';
import { hasTrackedView, markTrackedView, formatEngagementCount } from '../utils/contentEngagement';
import './VillageNewsDetail.css';

/**
 * VillageNewsDetail Page
 * Displays full village news article with details, sharing options, and related news
 */

// YouTube URL converter utility
const getYouTubeEmbedUrl = (url) => {
  if (!url) return null;
  
  // Already in embed format
  if (url.includes('youtube.com/embed/')) {
    return url;
  }
  
  // Extract video ID from different YouTube URL formats
  let videoId = null;
  
  // Format: https://www.youtube.com/watch?v=VIDEO_ID
  if (url.includes('youtube.com/watch?v=')) {
    videoId = url.split('v=')[1].split('&')[0];
  }
  // Format: https://youtu.be/VIDEO_ID
  else if (url.includes('youtu.be/')) {
    videoId = url.split('youtu.be/')[1].split('?')[0];
  }
  // Format: https://www.youtube.com/watch?v=VIDEO_ID (with additional parameters)
  else if (url.includes('youtube.com') && url.includes('watch')) {
    const params = new URLSearchParams(new URL(url).search);
    videoId = params.get('v');
  }
  
  // Return embed URL if we found video ID
  if (videoId) {
    return `https://www.youtube.com/embed/${videoId}`;
  }
  
  // Return original URL if not a YouTube URL
  return url;
};

function VillageNewsDetail() {
  const { newsId } = useParams();
  const navigate = useNavigate();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchNewsDetail = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(buildApiUrl(`/news/id/${newsId}`));
      const data = await response.json();

      console.log('📰 VillageNewsDetail: Fetched news:', data);
      console.log('🎥 Video URL from API:', data.data?.videoUrl);
      console.log('📸 Image from API:', data.data?.image);
      console.log('🏘️ Village Name:', data.data?.villageName);

      if (data.success) {
        setNews(data.data);
      } else {
        setError('समाचार नहीं मिला');
      }
    } catch (error) {
      console.error('Error fetching news:', error);
      setError('समाचार लोड करने में विफल रहे');
    } finally {
      setLoading(false);
    }
  }, [newsId]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchNewsDetail();
  }, [fetchNewsDetail]);

  useEffect(() => {
    if (!news?._id || hasTrackedView('news', news._id)) {
      return;
    }

    const trackView = async () => {
      try {
        const response = await newsAPI.incrementNewsView(news._id);

        if (response.success && response.data?.views !== undefined) {
          setNews((current) => current ? { ...current, views: response.data.views, shareCount: response.data.shareCount ?? current.shareCount } : current);
        }

        markTrackedView('news', news._id);
      } catch (error) {
        console.error('Failed to increment village news view count:', error);
      }
    };

    trackView();
  }, [news?._id]);

  if (loading) {
    return (
      <div className="village-news-detail loading">
        <div className="spinner">लोड हो रहा है...</div>
      </div>
    );
  }

  if (error || !news) {
    return (
      <div className="village-news-detail error">
        <div className="error-container">
          <h2>❌ त्रुटि</h2>
          <p>{error || 'समाचार नहीं मिला'}</p>
          <button className="back-btn" onClick={() => navigate(-1)}>
            वापस जाएं
          </button>
        </div>
      </div>
    );
  }

  const newsUrl = `${window.location.origin}/village-news/${news._id}`;
  const formattedDate = new Date(news.createdAt).toLocaleDateString('hi-IN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long'
  });

  return (
    <article className="village-news-detail">
      <div className="village-news-detail-container">
        {/* Main Content */}
        <div className="village-news-detail-main">
          {/* Back Button */}
          <button className="back-btn-main" onClick={() => navigate(-1)}>
            ← वापस जाएं
          </button>

          {/* Header */}
          <header className="news-header">
            <h1 className="news-title">{news.title}</h1>

            <div className="news-meta">
              <span className="category-badge">{news.category}</span>
              {news.villageName && <span className="location-badge">📍 गाँव: {news.villageName}</span>}
              {news.blockName && <span className="location-badge">📍 ब्लॉक: {news.blockName}</span>}
              {news.districtName && <span className="location-badge">📍 जिला: {news.districtName}</span>}
              <span className="date-badge">📅 {formattedDate}</span>
              <span className="date-badge">👁️ {formatEngagementCount(news.views)} views</span>
              <span className="date-badge">🔗 {formatEngagementCount(news.shareCount)} shares</span>
            </div>

            <p className="news-short-description">{news.shortDescription}</p>
          </header>

          {/* Featured Image */}
          {news.image && (
            <figure className="news-feature-image">
              <img 
                src={news.image} 
                alt={news.title}
                loading="lazy"
              />
            </figure>
          )}

          {/* Content */}
          <div className="news-content">
            <p>{news.description}</p>
          </div>

          {/* Video if available */}
          {news.videoUrl && (
            <div className="news-video-container">
              <h3>📹 वीडियो</h3>
              <div className="news-video">
                {(() => {
                  const embedUrl = getYouTubeEmbedUrl(news.videoUrl);
                  console.log('🎬 Embedding video:');
                  console.log('   Original URL:', news.videoUrl);
                  console.log('   Embed URL:', embedUrl);
                  return (
                    <iframe
                      width="100%"
                      height="500"
                      src={embedUrl}
                      title={news.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      referrerPolicy="strict-origin-when-cross-origin"
                      sandbox="allow-same-origin allow-scripts allow-popups allow-presentation"
                    />
                  );
                })()}
              </div>
            </div>
          )}

          {/* Tags */}
          {news.tags && news.tags.length > 0 && (
            <div className="news-tags">
              {news.tags.map((tag, index) => (
                <span key={index} className="tag">
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* Share Buttons */}
          <div className="share-section">
            <ShareButtons 
              title={news.title}
              url={newsUrl}
              description={news.shortDescription}
              contentId={news._id}
              contentType="news"
            />
          </div>

          {/* Related News */}
          {news.village && (
            <RelatedVillageNews 
              villageId={news.village} 
              currentNewsId={news._id}
              villageName={news.villageName}
            />
          )}
        </div>

        {/* Sidebar */}
        <aside className="village-news-detail-sidebar">
          {/* News Info Widget */}
          <div className="sidebar-widget">
            <h4>📰 समाचार विवरण</h4>
            {news.villageName && (
              <p className="info-item">
                <strong>गाँव:</strong> {news.villageName}
              </p>
            )}
            {news.blockName && (
              <p className="info-item">
                <strong>ब्लॉक:</strong> {news.blockName}
              </p>
            )}
            {news.districtName && (
              <p className="info-item">
                <strong>जिला:</strong> {news.districtName}
              </p>
            )}
            <p className="info-item">
              <strong>श्रेणी:</strong> {news.category}
            </p>
            <p className="info-item">
              <strong>तारीख:</strong> {formattedDate}
            </p>
          </div>

          {/* Share Widget */}
          <div className="sidebar-widget">
            <h4>🔖 साझा करें</h4>
            <ShareButtons
              title={news.title}
              url={newsUrl}
              description={news.shortDescription}
              contentId={news._id}
              contentType="news"
              compact
            />
          </div>
        </aside>
      </div>
    </article>
  );
}

export default VillageNewsDetail;
